import { useCallback, useMemo, useRef } from 'react';
import { doNothing } from '@ricoh/ria.client.core/constants/core/action';
import { ANIMATION_DELAY } from '@ricoh/ria.client.core/constants/ui/animation';

import type { LoadingScreenHook } from './loading.type';

/**
 * Use LoadingScreen.
 *
 * @returns {LoadingScreenHook} LoadingScreen hook.
 */
export function useLoadingScreen(): LoadingScreenHook {
  const loadingScreenRef = useRef<HTMLElement>(document.querySelector('#loading'));

  const hide = useCallback<LoadingScreenHook['hide']>((delay = ANIMATION_DELAY.VERY_FAST) => {
    setTimeout(() => {
      const loadingScreenEl = loadingScreenRef.current;
      if (!loadingScreenEl) {
        return;
      }

      loadingScreenEl.classList.add('transition-fade-out');
      loadingScreenEl.classList.remove('transition-fade-in');
    }, delay);
  }, []);

  const show = useCallback<LoadingScreenHook['show']>(
    (opacity = 0.9, delay = ANIMATION_DELAY.VERY_FAST) => {
      const loadingScreenEl = loadingScreenRef.current;
      if (!loadingScreenEl) {
        return {
          [Symbol.dispose]: doNothing,
        };
      }

      loadingScreenEl.style.opacity = opacity.toString();
      loadingScreenEl.classList.add('transition-fade-in');
      loadingScreenEl.classList.remove('transition-fade-out');

      return {
        [Symbol.dispose]: () => {
          hide(delay);
        },
      };
    },
    [hide],
  );

  return useMemo<LoadingScreenHook>(
    () => ({
      show,
      hide,
    }),
    [hide, show],
  );
}
